import React, {
  useEffect,
} from 'react';
import clsx from 'clsx';
import {
  useForm,
  FormProvider,
} from 'react-hook-form';
import noop from 'lodash/noop';
import Controller from 'core/components/FormController';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Input from 'pages/Dashboard/components/Input';
import Dialog from 'pages/Dashboard/components/Dialog';
import PhoneInput from 'core/components/PhoneInput';
import Select from 'core/components/Select';
import Button from 'pages/Dashboard/components/Button';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import dayjs from 'utils/dayjs';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  validateEmailIfSet,
  validateDateIfFuture,
  validatePatientName,
  compactPatient,
  validatePhone,
  validatePhoneIfSet,
} from 'pages/Dashboard/utils/helper';
import {
  PatientFormData,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/patient';
import {
  serializePatientForm,
  getDefaultPatientData,
  genders,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog/helpers';
import {
  useSavePatient,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import ExtraPatientFields from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog/ExtraPatientFields';
import {
  useNotify,
} from 'core/hooks';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  useGetPhoneNumberTag,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  CreatePatientRequestDTO,
} from 'dtos';

export type PatientDialogParams = {
  defaultPhoneNumber?: string;
  onPatientSelect?: (patient: PatientCompact) => void;
  onCancel?: () => void;
  dialogHeight?: number;
  hasBackdrop?: boolean;
};

type Props = PatientDialogParams & {
  onClose: () => void;
};

function getDatePickerValue(value: unknown) {
  return typeof value === 'string' || value instanceof Date || dayjs.isDayjs(value)
    ? value
    : undefined;
}

function getStringValue(value: unknown) {
  return typeof value === 'string' ? value : '';
}

export default function CreatePatientDialog({
  onClose,
  defaultPhoneNumber,
  onPatientSelect,
  dialogHeight,
  hasBackdrop = false,
  onCancel,
}: Props) {
  const formProps = useForm<PatientFormData>({
    mode: 'onChange',
    defaultValues: getDefaultPatientData(),
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: {
      isValid,
      isSubmitting,
      isSubmitted,
      touchedFields,
    },
  } = formProps;
  const handleClose = () => {
    onClose();
    reset();
    onCancel?.();
  };

  const phoneNumber = watch('phoneNumber');

  const {
    phoneTag: primaryPhoneTag,
    isLoading: isLoadingPrimaryPhoneTag,
  } = useGetPhoneNumberTag(phoneNumber);

  useEffect(() => {
    let _phoneNumber = defaultPhoneNumber ?? '';

    if (_phoneNumber.length === 10) {
      _phoneNumber = '1'.concat(_phoneNumber);
    }

    if (!/^\+\d+$/?.test(_phoneNumber)) {
      _phoneNumber = '+'.concat(_phoneNumber);
    }

    setValue('phoneNumber', _phoneNumber);
  }, [defaultPhoneNumber]);

  const notify = useNotify();
  const shouldShowPhoneError = isSubmitted || Boolean(touchedFields.phoneNumber);

  const {
    mutateAsync: savePatient,
    isPending: savingPatients,
  } = useSavePatient();

  const onSubmit = async (formData: PatientFormData) => {
    const payload = serializePatientForm(formData);
    try {
      const newPatient = await savePatient(payload as CreatePatientRequestDTO);
      onPatientSelect?.(compactPatient(newPatient));
      notify({
        variant: 'success',
        message: 'Patient saved successfully!',
      });
      reset();
      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog
      open
      title="Create Patient"
      onClose={handleClose}
      onConfirm={noop}
      confirmText="Create"
      className="nw-create-patient-dialog !w-[48rem] !space-y-0 overflow-y-hidden !p-0 max-h-[80vh]"
      titleClassName="p-5 pb-2 shrink-0"
      dialogClassName={clsx({ 'hidden-backdrop': !hasBackdrop })}
      maxWidth="lg"
      disableEscapeKeyDown
      disableBackdropClick
      minHeight={dialogHeight}
    >
      <FormProvider {...formProps}>
        <div className="flex flex-1 min-h-0 flex-col gap-form-row overflow-y-auto px-5 pt-2 text-gray-500 text-xs">
          <div className="flex flex-col w-full gap-form-row">
            <div className="flex gap-form-field">
              <Controller
                name="firstName"
                control={control}
                rules={{
                  validate: (value) => validatePatientName(value, 'Enter a valid first name'),
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    className="flex-1 min-w-0"
                    id="firstName"
                    inputProps={{ autoComplete: 'off' }}
                    label="First Name"
                    required
                    placeholder="First Name"
                  />
                )}
              />
              <Controller
                name="middleName"
                control={control}
                rules={{
                  validate: (value) => validatePatientName(value, 'Enter a valid middle name', false),
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    inputProps={{ autoComplete: 'off' }}
                    className="flex-1 min-w-0"
                    id="middleName"
                    label="Middle Name"
                    placeholder="Middle Name"
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                rules={{
                  validate: (value) => validatePatientName(value, 'Enter a valid last name'),
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    inputProps={{ autoComplete: 'off' }}
                    className="flex-1 min-w-0"
                    id="lastName"
                    label="Last Name"
                    required
                    placeholder="Last Name"
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-3 gap-form-field">
              <Controller
                name="dateOfBirth"
                control={control}
                rules={{
                  required: 'Enter date of birth',
                  validate: (value) => (validateDateIfFuture(value) ? true : 'Enter valid date of birth'),
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Wrapper
                    id="date-of-birth"
                    label="Date of birth"
                    className="min-w-0"
                    error={invalid}
                    showErrorText={false}
                    required
                  >
                    <DatePicker
                      value={getDatePickerValue(field.value)}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      containerClassName="!w-full"
                      inputContainerClassName="!w-full"
                      className="w-full"
                      disableFuture
                      showDateButtons={false}
                    />
                  </Wrapper>
                )}
              />
              <Controller
                name="genderDescription"
                control={control}
                rules={{ required: 'Select gender' }}
                render={({ field, fieldState: { invalid } }) => (
                  <Select<string>
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    id="gender"
                    className="min-w-0 text-gray-500 text-xs"
                    label="Gender"
                    placeholder="Select"
                    options={genders}
                    getLabel={(option: string) => option}
                    getValue={(option: string) => option}
                    required
                  />
                )}
              />
            </div>
            <div className="flex gap-form-field">
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: 'Enter phone number',
                  validate: (value) => (validatePhone(getStringValue(value)) ? true : 'Enter valid phone number'),
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Wrapper
                    id="cell-phone-number"
                    error={shouldShowPhoneError && invalid}
                    showErrorText={false}
                    label="Phone"
                    className="flex-1 min-w-0"
                    required
                  >
                    <PhoneInput
                      value={getStringValue(field.value)}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isLoadingPhoneTag={isLoadingPrimaryPhoneTag}
                      phoneTag={primaryPhoneTag}
                      error={shouldShowPhoneError && invalid}
                      id="cell-phone-number"
                      name="cell-phone-number"
                      className="nw-primary-phone-number"
                      inputProps={{ autoComplete: 'off' }}
                      placeholder="Phone"
                    />
                  </Wrapper>
                )}
              />
              <Controller
                name="otherPhoneNumber"
                control={control}
                rules={{ validate: validatePhoneIfSet }}
                render={({ field, fieldState: { invalid } }) => (
                  <Wrapper
                    id="other-phone-number"
                    error={invalid}
                    showErrorText={false}
                    label="Other Phone"
                    className="flex-1 min-w-0"
                  >
                    <PhoneInput
                      value={getStringValue(field.value)}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={invalid}
                      id="cell-phone-number"
                      name="cell-phone-number"
                      placeholder="Other Phone"
                      inputProps={{ autoComplete: 'off' }}
                    />
                  </Wrapper>
                )}
              />
              <Controller
                name="email"
                control={control}
                rules={{ validate: validateEmailIfSet }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    className="flex-1 min-w-0"
                    id="email"
                    label="Email"
                    type="email"
                    placeholder="Email"
                  />
                )}
              />
            </div>
            <ExtraPatientFields />
          </div>
        </div>
        <div className="flex shrink-0 flex-row-reverse items-center justify-between p-5">
          <DisabledFormTooltip>
            <Button
              className="!py-1.5 !ml-3"
              variant="contained"
              type="submit"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting || savingPatients}
            >
              Create
            </Button>
          </DisabledFormTooltip>
          <Button
            className="!py-1.5 ml-3"
            variant="outlined"
            type="button"
            onClick={handleClose}
            disabled={isSubmitting || savingPatients}
          >
            Close
          </Button>
        </div>
      </FormProvider>
    </Dialog>
  );
}
